import "./style.scss";

import CloudOffIcon from "@mui/icons-material/CloudOff";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PowerOffOutlinedIcon from "@mui/icons-material/PowerOffOutlined";
import { Tooltip } from "@mui/material";
import StyledButton from "components/shared/StyledButton";
import { useTranslation } from "react-i18next";
import type { IIntegrations } from "store/integrationsStore";
import { useAppSelector } from "store/store";

/**
 * This is an integration block, ex : Google integration block
 */
interface IIntegrationBlock {
    title: string;
    name: string;
    /**
     * Icon of the integration
     */
    icon: React.ReactNode;
    subtitle: string;
    /**
     * Verify if the user is ever connected to that integration
     */
    isConnected: boolean;
    /**
     * Action onClick the connect button of an integration
     */
    onConnect: () => void;
    /**
     * Action onClick disconnect button of an integration
     */
    onDisconnect: ((e: React.MouseEvent) => void) | null;
    /**
     * Action onClick an integration, it open a drawer with details about it
     */
    onOpenDetails: () => void;
    integrations?: IIntegrations[];
}

const IntegrationBlock = ({
    title,
    name,
    icon,
    subtitle,
    isConnected,
    onConnect,
    onDisconnect,
    onOpenDetails,
    integrations,
}: IIntegrationBlock) => {
    const [t] = useTranslation();
    const authGoogleStatus = useAppSelector((state) => state.integrations.authGoogleStatus);
    const usersNumber = integrations?.length;

    const isLoading = {
        google: authGoogleStatus === "pending",
        sage: false,
        ebp: false,
        dilisco: false,
        interforum: false,
    };

    const handleConnect = (e) => {
        e.stopPropagation();
        onConnect();
    };

    const handleDisconnect = (e) => {
        e.stopPropagation();
        onDisconnect && onDisconnect(e);
    };

    const handleOpenDetails = () => {
        if (usersNumber) onOpenDetails();
    };

    return (
        <div className={`integration-block ${!usersNumber ? "disabled" : "available"}`} onClick={handleOpenDetails}>
            <div className="integration-block-head">
                {icon}
                {!isConnected && (
                    <StyledButton
                        buttonType="secondary"
                        style={{ width: "97px" }}
                        onClick={handleConnect}
                        loading={isLoading[name]}
                        disabled={isLoading[name]}
                    >
                        {!isLoading[name] && t("IntegrationsSettings.Connect")}
                    </StyledButton>
                )}
                {isConnected && onDisconnect && (
                    <Tooltip title={`${t("IntegrationsSettings.Disconnect")} ${title}`} placement="top" arrow>
                        <button className="disconnect" onClick={handleDisconnect}>
                            <PowerOffOutlinedIcon className="disconnect-icon" />
                        </button>
                    </Tooltip>
                )}
            </div>
            <div className="integration-block-body">
                <div className="integration-block-title-wrapper">
                    {title}
                    <IntegrationChip isActive={isConnected} />
                    {!!usersNumber && (
                        <div className="integration-block-users-nb">
                            {usersNumber == 1 ? <PersonOutlineOutlinedIcon /> : <PeopleAltOutlinedIcon />}
                            <span>{usersNumber}</span>
                        </div>
                    )}
                </div>
                <span className="integration-block-subtitle">{subtitle}</span>
            </div>
        </div>
    );
};

export default IntegrationBlock;

interface IIntegrationChip {
    isActive: boolean;
}
const IntegrationChip = ({ isActive }: IIntegrationChip) => {
    const [t] = useTranslation();

    return (
        <div className="integration-block-chip">
            {isActive ? (
                <div className="chip">
                    <div className="chip-light" />
                    {t("IntegrationsSettings.Connected")}
                </div>
            ) : (
                <div className="integration-block-chip off">
                    <CloudOffIcon className="cloud-off" />
                    {t("IntegrationsSettings.NotConnected")}
                </div>
            )}
        </div>
    );
};
